import { c as _c } from "react/compiler-runtime";
import defaultGroupThumbnail from "@assets/thumbnails/groups-thumbs/group-thumbnail_default.svg";
import { DropdownFilterChip } from "@components/common/faro-table/faro-table-filter/dropdown-filter-chip";
import { ThumbnailImage } from "@components/common/thumbnail-image";
import { getGroupFilterOption, UNKNOWN_GROUP_LABEL } from "@components/projects/projects-filters/projects-group-filter-utils";
import { useFetchGroupsOnce } from "@hooks/use-fetch-groups-once";
import { useFilterQueryParams } from "@hooks/table-filters/use-filter-query-params";
import { fetchingGroupsFlagsSelector, groupsSelector } from "@store/groups/groups-selector";
import { selectedSdbCompanyIdSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";
/**
 * A chip to filter the projects by group. It offers all the groups the user has access to, which
 * are only fetched when the chip is first opened in the workspace, unless they already were. The
 * user can search the groups and apply several of them at once.
 */
export function GroupFilterChip(t0) {
  const $ = _c(16);
  const {
    filteredColumn,
    label,
    tableType
  } = t0;
  const companyId = useAppSelector(selectedSdbCompanyIdSelector);
  const fetchGroupsOnce = useFetchGroupsOnce(companyId);
  const groups = useAppSelector(groupsSelector);
  const {
    isFetchingGroups
  } = useAppSelector(fetchingGroupsFlagsSelector);
  const {
    getFilterIdsForColumn
  } = useFilterQueryParams();
  let T0;
  let t1;
  let t2;
  let t3;
  if ($[0] !== filteredColumn || $[1] !== getFilterIdsForColumn || $[2] !== groups || $[3] !== label) {
    const activeFilterIds = getFilterIdsForColumn(filteredColumn);
    T0 = DropdownFilterChip;
    t1 = label;
    t2 = filteredColumn;
    t3 = getGroupFilterItems(groups, activeFilterIds);
    $[0] = filteredColumn;
    $[1] = getFilterIdsForColumn;
    $[2] = groups;
    $[3] = label;
    $[4] = T0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    T0 = $[4];
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
  }
  let t4;
  if ($[8] !== T0 || $[9] !== fetchGroupsOnce || $[10] !== isFetchingGroups || $[11] !== t1 || $[12] !== t2 || $[13] !== t3 || $[14] !== tableType) {
    t4 = <T0 label={t1} filteredColumn={t2} items={t3} shouldShowSearchBox={true} shouldShowCheckbox={true} tableType={tableType} size="large" isLoading={isFetchingGroups} onOpen={fetchGroupsOnce} />;
    $[8] = T0;
    $[9] = fetchGroupsOnce;
    $[10] = isFetchingGroups;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
    $[14] = tableType;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
}

/**
 * Returns the filters for the groups sorted by name, each with its thumbnail. The applied groups
 * are listed too, even if the user does not see them, e.g. from an old link, so they can be removed.
 */
function getGroupFilterItems(groups, activeFilterIds) {
  const groupFilters = [...groups].sort((a, b) => a.name.localeCompare(b.name)).map(group => ({
    ...getGroupFilterOption(group),
    icon: <GroupThumbnail thumbnailUrl={group.thumbnailUrl} />
  }));
  const knownGroupIds = new Set(groups.map(({
    id
  }) => id));
  const unknownGroupFilters = activeFilterIds.filter(filterId => !knownGroupIds.has(filterId)).map(filterId => ({
    ...getGroupFilterOption({
      id: filterId,
      name: UNKNOWN_GROUP_LABEL
    }),
    icon: <GroupThumbnail />
  }));
  return Object.fromEntries([...groupFilters, ...unknownGroupFilters].map(filter => [filter.id, filter]));
}

/** The thumbnail of a group, or the default one if it has none */
function GroupThumbnail(t0) {
  const $ = _c(3);
  const {
    thumbnailUrl
  } = t0;
  const t1 = thumbnailUrl || undefined;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      height: "1.5rem",
      width: "2.125rem",
      flexShrink: 0,
      objectFit: "contain",
      borderRadius: "0.25rem"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== t1) {
    t3 = <ThumbnailImage src={t1} fallbackSrc={defaultGroupThumbnail} alt="" sx={t2} />;
    $[1] = t1;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  return t3;
}